import { Alert, Button, Stack } from "@mui/material";
import * as React from "react";
import { useLanguage } from "./Language";
import * as dayjs from "dayjs";
import { Link } from "react-router-dom";
import { BorrowerNotificationType } from "./OBRSettings";

export interface BorrowerChild {
  id: number;
  firstName: string;
  lastName: string;
  issuingLocation: number;
}

export enum Gender {
  Male="Male",
  Female="Female",
  Unspecified="Unspecified",
}

export interface Patron {
  guid: string;
  patronId: string;
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: any;
  fullName: string;
  pin: string;
  email: any;
  email2: any;
  email3: any;
  phone: string;
  altPhone: any;
  status: string;
  fees: number;
  itemsOverdue: number;
  loanCount: number;
  arrivedHolds: number;
  pendingHolds: number;
  lostCount: any;
  address1: string;
  address2: any;
  address3: any;
  city: string;
  state: string;
  zipcode: string;
  secondaryAddress1: any;
  secondaryAddress2: any;
  secondaryAddress3: any;
  secondaryCity: any;
  secondaryState: any;
  secondaryZipcode: any;
  country: any;
  cardExpirationDate: number;
  dob: any;
  gender: Gender;
  issuingBranchId: string;
  salutation: any;
  defaultRequestPickupBranch: number;
  googleId: any;
  googleEmail: any;
  responsibleAgencyNumber: any;
  smsPhone: any;
  smsGatewayId: any;
  expiredCardNotificationTypes: BorrowerNotificationType[];
  canBorChangeAccntExpire: boolean;
  friendlyWarningNotificationTypes: BorrowerNotificationType[];
  canBorChangeFriendlyWarn: boolean;
  overdueNotificationTypes: BorrowerNotificationType[];
  canBorChangeOverdue: boolean;
  finalOverdueNotificationTypes: BorrowerNotificationType[];
  canBorChangeFinalOverdue: boolean;
  arrivedHoldNotificationTypes: BorrowerNotificationType[];
  canBorChangeArrivedHold: boolean;
  cancelledHoldNotificationTypes: BorrowerNotificationType[];
  canBorChangeCanceledHld: boolean;
  automaticRenewalNotificationTypes: BorrowerNotificationType[];
  canBorChangeAutoRenewal: boolean;
  ableToAddDependentsFromPac: boolean;
  children?: BorrowerChild[];
  cardExpirationDateString: string;
  issuingBranch: number;
}

export interface AccountSummary {
  status: string;
  fees: number;
  itemsOverdue: number;
  loanCount: number;
  arrivedHolds: number;
  pendingHolds: number;
  lostCount: number;
  savedSearchCount: number;
  listCount: number;
  digitalInformationLastUpdated: number;
}

export interface GetAccountSummaryReturn {
  patron: Patron;
  accountSummary: AccountSummary;
}

export interface AccountSummaryAlertsProps {
  patron: Patron;
  accountSummary: AccountSummary;
}

export const AccountSummaryAlerts: React.FC<AccountSummaryAlertsProps> = (
  props
) => {
  const { patron, accountSummary } = props;
  const isExpired = dayjs().isAfter(dayjs(patron.cardExpirationDate), "day");
  const { translate } = useLanguage();

  const convertPenniesToCurrency = (value: number, locale = "en-US") => {
    const pennies = !value || isNaN(value) ? 0 : value;
    const dollars = pennies / 100;
    const formattedCurrency = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(dollars);

    return formattedCurrency;
  };

  return (
    <Stack sx={{ width: "100%" }} spacing={2}>
      {isExpired && <Alert severity="error">{translate("expiredCard")}</Alert>}
      {accountSummary.itemsOverdue > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              component={Link}
              to="/myaccount/loans"
              color="inherit"
              size="small"
            >
              {translate("viewLoans")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.itemsOverdue}</strong>{" "}
          {translate("itemsOverdue")}
        </Alert>
      )}

      {accountSummary.fees > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              component={Link}
              to="/myaccount/fines"
              color="inherit"
              size="small"
            >
              {translate("viewFines")}
            </Button>
          }
        >
          {translate("youOwe")}{" "}
          <strong>{convertPenniesToCurrency(patron.fees)}</strong>{" "}
          {translate("fees")}
        </Alert>
      )}

      {accountSummary.arrivedHolds > 0 && (
        <Alert
          severity="error"
          action={
            <Button
              component={Link}
              to="/myaccount/holds"
              color="inherit"
              size="small"
            >
              {translate("viewHolds")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.arrivedHolds}</strong>{" "}
          {translate("arrivedHolds")}
        </Alert>
      )}

      {accountSummary.loanCount > 0 && (
        <Alert
          severity="info"
          action={
            <Button
              component={Link}
              to="/myaccount/loans"
              color="inherit"
              size="small"
            >
              {translate("viewLoans")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.loanCount}</strong>{" "}
          {translate("loanCount")}
        </Alert>
      )}

      {accountSummary.pendingHolds > 0 && (
        <Alert
          severity="info"
          action={
            <Button
              component={Link}
              to="/myaccount/holds"
              color="inherit"
              size="small"
            >
              {translate("viewHolds")}
            </Button>
          }
        >
          {translate("youHave")} <strong>{accountSummary.pendingHolds}</strong>{" "}
          {translate("pendingHolds")}
        </Alert>
      )}
    </Stack>
  );
};
